class SequenceCanvas extends HTMLElement {
  static get observedAttributes() {
    return [
      'duration',
      'display-width',
      'loop',
      'delay',
      'hide-after-play',
      'min-width',
      'autoplay',
      'src-template',
      'frames',
      'start',
      'pad',
    ];
  }

  constructor() {
    super();
    this._images = [];
    this._loaded = 0;
    this._currentFrame = 0;
    this._firstFrameDrawn = false;
    this._raf = null;
    this._animationStartTime = null;

    this._onResize = this._onResize.bind(this);
    this._tick = this._tick.bind(this);
  }

  connectedCallback() {
    this._canvas = this.querySelector('canvas');
    if (!this._canvas) return;

    this._ctx = this._canvas.getContext('2d');
    if (!this._ctx) return;

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const minWidth = this._numAttr('min-width', 0);
    if (minWidth && window.innerWidth < minWidth) return;

    const frameUrls = this._getFrames();
    if (!frameUrls || !frameUrls.length) return;

    this._totalFrames = frameUrls.length;
    this._preload(frameUrls);

    window.addEventListener('resize', this._onResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    this.stop();
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this._measureFromFirstImage();
  }

  play() {
    if (!this._images.length) return;
    this.stop();
    this._animationStartTime = null;
    this._currentFrame = 0;
    this._raf = requestAnimationFrame(this._tick);
  }

  stop() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = null;
  }

  _getFrames() {
    const tpl = this.getAttribute('src-template');
    const count = this._numAttr('frames', 0);
    if (tpl && count > 0) {
      const start = this._numAttr('start', 0); // start index
      const pad = this._numAttr('pad', 0);     // 0 = no padding, 2 => 00,01...
      return Array.from({ length: count }, (_, i) => {
        const n = start + i;
        const s = pad > 0 ? String(n).padStart(pad, '0') : String(n);
        return tpl.replace('{n}', s);
      });
    }

    // const attr = this.getAttribute('frames');
    // if (attr) {
    //   try {
    //     const parsed = JSON.parse(attr);
    //     if (Array.isArray(parsed)) return parsed;
    //   } catch {}
    // }

    return null;
  }

  _preload(frameUrls) {
    this._images = new Array(frameUrls.length);
    this._loaded = 0;
    this._firstFrameDrawn = false;

    frameUrls.forEach((url, index) => {
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = url;

      img.onload = () => {
        this._loaded++;

        if (index === 0 && !this._firstFrameDrawn) {
          this._firstFrameDrawn = true;
          this._measureAndDraw(img, 0);
        }

        if (this._loaded === frameUrls.length) {
          this._measureFromFirstImage();

          if (this._boolAttr('autoplay', true)) {
            const delay = this._numAttr('delay', 0);
            if (delay > 0) setTimeout(() => this.play(), delay);
            else this.play();
          }
        }
      };

      img.onerror = () => {
        this._loaded++;
        if (this._loaded === frameUrls.length) {
          this._measureFromFirstImage();
          if (this._boolAttr('autoplay', true)) this.play();
        }
      };

      this._images[index] = img;
    });
  }

  _tick(timestamp) {
    if (!this._images[0]?.naturalWidth) {
      this._raf = requestAnimationFrame(this._tick);
      return;
    }

    if (!this._animationStartTime) this._animationStartTime = timestamp;

    const duration = this._numAttr('duration', 1.6);
    const elapsed = (timestamp - this._animationStartTime) / 1000;
    const progress = Math.min(elapsed / Math.max(0.001, duration), 1);

    const total = this._totalFrames ?? this._images.length;
    this._currentFrame = Math.floor(progress * (total - 1));

    this._drawCurrent();

    if (progress < 1) {
      this._raf = requestAnimationFrame(this._tick);
      return;
    }

    if (this._boolAttr('loop', false)) {
      this._animationStartTime = null;
      this._currentFrame = 0;
      this._raf = requestAnimationFrame(this._tick);
      return;
    }

    if (this._boolAttr('hide-after-play', false)) {
      this._canvas.classList.add('hidden');
    }

    this.stop();
  }

  _onResize() {
    this._measureFromFirstImage();
    this._drawCurrent();
  }

  _measureFromFirstImage() {
    const first = this._images.find(img => img?.naturalWidth);
    if (!first) return;
    this._measureAndDraw(first, this._currentFrame);
  }

  _measureAndDraw(img, frameIndex) {
    const dpr = window.devicePixelRatio || 1;

    const naturalWidth = img.naturalWidth || 1;
    const naturalHeight = img.naturalHeight || 1;
    const aspectRatio = naturalHeight / naturalWidth;

    const displayWidth = this._numAttr('display-width', 25); // vw
    const cssWidth = window.innerWidth * (displayWidth / 100);
    const cssHeight = cssWidth * aspectRatio;

    const nextW = Math.round(cssWidth * dpr);
    const nextH = Math.round(cssHeight * dpr);

    if (this._canvas.width !== nextW) this._canvas.width = nextW;
    if (this._canvas.height !== nextH) this._canvas.height = nextH;

    this._canvas.style.width = `${cssWidth}px`;
    this._canvas.style.height = `${cssHeight}px`;

    this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this._currentFrame = frameIndex ?? 0;
    this._drawCurrent(cssWidth, cssHeight);
  }

  _drawCurrent(cssWidthOverride, cssHeightOverride) {
    const img = this._images[this._currentFrame];
    if (!img || !img.complete) return;

    const cssWidth =
      cssWidthOverride ?? (parseFloat(this._canvas.style.width) || (this._canvas.width / (window.devicePixelRatio || 1)));
    const cssHeight =
      cssHeightOverride ?? (parseFloat(this._canvas.style.height) || (this._canvas.height / (window.devicePixelRatio || 1)));

    this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    this._ctx.drawImage(img, 0, 0, cssWidth, cssHeight);
  }

  _numAttr(name, fallback) {
    const v = Number(this.getAttribute(name));
    return Number.isFinite(v) ? v : fallback;
  }

  _boolAttr(name, fallback = false) {
    if (!this.hasAttribute(name)) return fallback;
    const v = this.getAttribute(name);
    if (v === '' || v === name) return true;
    return v === 'true' || v === '1';
  }
}

if (!customElements.get('sequence-canvas')) {
  customElements.define('sequence-canvas', SequenceCanvas);
}
